import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BsBook, BsBuilding, BsChevronRight,
  BsFileText, BsRobot, BsClipboardData, BsGraphUp,
  BsLightningCharge, BsCheckCircle,
} from 'react-icons/bs';
import { trackEvent } from '../../utils/analytics';

const schoolBoards = [
  { name: 'CBSE', descriptor: 'Classes 6–12', color: 'from-blue-500 to-indigo-600' },
  { name: 'ICSE', descriptor: 'Classes 6–10', color: 'from-emerald-500 to-teal-600' },
  { name: 'IGCSE', descriptor: 'Cambridge Curriculum', color: 'from-orange-500 to-amber-600' },
  { name: 'IB', descriptor: 'International Baccalaureate', color: 'from-purple-500 to-violet-600' },
  { name: 'Kerala State', descriptor: 'SCERT Syllabus', color: 'from-pink-500 to-rose-600' },
];

const schoolBenefits = [
  { icon: BsFileText, label: 'Past Papers' },
  { icon: BsRobot, label: 'AI Explanations' },
  { icon: BsClipboardData, label: 'Custom Question Papers' },
  { icon: BsGraphUp, label: 'Subject-wise Practice' },
];

const engFeatures = [
  { icon: BsBook, title: 'Semester-wise Practice', description: 'Organized by semester and subject for focused study.', color: 'from-brand-500 to-blue-600' },
  { icon: BsFileText, title: 'Previous Year Papers', description: 'Access real university exam papers from past years.', color: 'from-accent-500 to-purple-600' },
  { icon: BsRobot, title: 'AI Doubt Assistance', description: 'Get instant answers to engineering concepts and problems.', color: 'from-emerald-500 to-teal-600' },
  { icon: BsClipboardData, title: 'Question Generator', description: 'Build custom practice papers from any topic or chapter.', color: 'from-orange-500 to-red-600' },
];

const engBenefits = [
  { icon: BsFileText, label: 'University Papers' },
  { icon: BsBook, label: 'Semester-wise Questions' },
  { icon: BsRobot, label: 'AI Explanations' },
  { icon: BsClipboardData, label: 'Custom Question Papers' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

const cardChild = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export default function LearningPaths() {
  return (
    <section id="learning-paths" className="relative">
      {/* ── School Section ── */}
      <div className="section-padding relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-600/10 rounded-full blur-[120px]" />

        <div className="container-custom mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-6">
              <BsBook className="text-brand-400 text-sm" />
              <span className="text-sm font-medium text-gray-300">For School Students</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-display text-white mb-4">
              📚 For School Students
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Practice past papers, generate custom question papers, and get instant AI-powered explanations.
            </p>
          </motion.div>

          {/* Board Cards */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mb-14"
          >
            {schoolBoards.map((board) => (
              <motion.div key={board.name} variants={cardChild}>
                <Link
                  to="/mode?category=school"
                  onClick={() => trackEvent("board_clicked", { board: board.name, source: "landing_learning_paths" })}
                  className="block group glass rounded-2xl p-6 hover:bg-white/[0.08] transition-all duration-300 hover:-translate-y-1 h-full text-center"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${board.color} flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform`}>
                    <BsBook className="text-xl text-white" />
                  </div>
                  <h3 className="text-lg font-bold font-display text-white mb-1">{board.name}</h3>
                  <p className="text-xs text-gray-400">{board.descriptor}</p>
                  <div className="flex items-center justify-center gap-1 mt-3 text-brand-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore <BsChevronRight className="text-[10px]" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {/* Benefits Row */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="flex flex-wrap justify-center gap-6 mb-10"
          >
            {schoolBenefits.map((b) => (
              <motion.div key={b.label} variants={cardChild} className="flex items-center gap-2 text-gray-300">
                <b.icon className="text-brand-400 text-lg" />
                <span className="text-sm font-medium">{b.label}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/mode?category=school" onClick={() => trackEvent("solve_clicked", { source: "landing_learning_paths_school" })} className="btn-primary text-lg !px-10 !py-4 group inline-flex items-center gap-2">
              <span className="relative z-10 flex items-center gap-2">
                Explore School Questions
                <BsChevronRight className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* ── Section Divider ── */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      {/* ── Engineering Section ── */}
      <div id="engineering" className="section-padding relative overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(99,102,241,0.04) 0%, transparent 100%)' }}>
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-accent-600/10 rounded-full blur-[120px]" />

        <div className="container-custom mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-6">
              <BsBuilding className="text-accent-400 text-sm" />
              <span className="text-sm font-medium text-gray-300">For Engineering Students</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-display text-white mb-4">
              🎓 For Engineering Students
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Master university exams with semester-wise practice and custom paper generation.
            </p>
          </motion.div>

          {/* Feature Cards */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14"
          >
            {engFeatures.map((feat) => (
              <motion.div key={feat.title} variants={cardChild}>
                <Link
                  to="/mode?category=college"
                  onClick={() => trackEvent("feature_clicked", { feature: feat.title, source: "landing_learning_paths_engineering" })}
                  className="block glass rounded-2xl p-6 h-full hover:bg-white/[0.08] transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feat.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feat.icon className="text-xl text-white" />
                  </div>
                  <h3 className="text-lg font-bold font-display text-white mb-2">{feat.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{feat.description}</p>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {/* Benefits Row */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="flex flex-wrap justify-center gap-6 mb-10"
          >
            {engBenefits.map((b) => (
              <motion.div key={b.label} variants={cardChild} className="flex items-center gap-2 text-gray-300">
                <b.icon className="text-accent-400 text-lg" />
                <span className="text-sm font-medium">{b.label}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/mode?category=college" onClick={() => trackEvent("solve_clicked", { source: "landing_learning_paths_engineering" })} className="btn-primary text-lg !px-10 !py-4 group inline-flex items-center gap-2">
              <span className="relative z-10 flex items-center gap-2">
                Explore Engineering Questions
                <BsChevronRight className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
